import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import { withSize } from 'react-sizeme';

import {
    Colours,
    Sizes,
    useSmoothCollapse,
    PageBehaviourButton,
    IconMinus,
    IconPlus,
    TooltipAnchor,
    CondensedTheme,
    Locales,
    TextAnchor,
} from '@ratehub/base-ui';
import {
    MORTGAGE_TERMS,
    RATE_TYPE_MESSAGES,
} from '@ratehub/mtg-common';

import BestMortgageRatesPropType from '../definitions/BestMortgageRatesPropType';
import { Messages as MORTGAGE_TERM_MESSAGES } from '../definitions/MortgageTerms';
import ProductTableBestMortgageRatesTableCell from './ProductTableBestMortgageRatesTableCell';


const DEFAULT_VISIBLE_TERMS = [ 24, 36, 60 ];

function ProductTableBestMortgageRatesTable({ rates, onRateClick, size, ...otherProps }) {
    const intl = useIntl();
    const containerRef = useRef(null);
    const [ isExpanded, toggleIsExpanded ] = useSmoothCollapse({ containerRef });

    /* TODO EVR-2469: This will be configurable via a prop and cleaned up */
    const isExpandable = rates.length > 3;
    const displayedRates = rates.map(rate => ({
        ...rate,
        isHidden: isRowHidden(rate, isExpandable, isExpanded),
    }));

    const isMoreDetailsInLightbox = size?.width < 475;

    return (
        <CondensedTheme>
            <div
                {...otherProps}
                ref={containerRef}
            >
                <Choose>
                    <When condition={rates.length > 0}>
                        <table className="best-mortgage-rates-table">
                            <thead>
                                <tr className="table-heading">
                                    <th className={TABLE_HEADER_CLASSES}>
                                        <FormattedMessage
                                            id="web-components.ProductTableBestMortgageRatesTable.termTableHeader"
                                            defaultMessage="Term"
                                        />
                                    </th>
                                    <th className={TABLE_HEADER_CLASSES}>
                                        <div className="table-header-tooltip-container">
                                            <FormattedMessage
                                                id="web-components.ProductTableBestMortgageRatesTable.fixedTableHeader"
                                                defaultMessage="Fixed"
                                            />
                                            <TooltipAnchor
                                                className="rh-ml-0_5"
                                                message={MESSAGES.FIXED_TOOLTIP}
                                                tooltipLocation="bottom"
                                                nubbinAlignment="middle"
                                                width="16em"
                                            />
                                        </div>
                                    </th>
                                    <th className={TABLE_HEADER_CLASSES}>
                                        <div className="table-header-tooltip-container">
                                            <FormattedMessage
                                                id="web-components.ProductTableBestMortgageRatesTable.variableTableHeader"
                                                defaultMessage="Variable"
                                            />
                                            <TooltipAnchor
                                                className="rh-ml-0_5"
                                                message={MESSAGES.VARIABLE_TOOLTIP}
                                                tooltipLocation="bottom"
                                                nubbinAlignment="end"
                                                width="16em"
                                            />
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <For
                                    each="row"
                                    of={displayedRates}
                                    index="index"
                                >
                                    <tr
                                        key={row.term}
                                        className={classNames('table-row', {
                                            'is-hidden': row.isHidden,
                                            'hide-bottom-border': !isExpandable && row === displayedRates[displayedRates.length - 1],
                                        })}
                                        aria-hidden={row.isHidden}
                                    >
                                        <td className="rh-p-1">
                                            <p className="rh-title-2xs weight-medium rh-mt-0 rh-mb-0_5">
                                                <Choose>
                                                    {/* HELOC has term 0 */}
                                                    <When condition={row.term === 0}>
                                                        <FormattedMessage {...MORTGAGE_TERM_MESSAGES.HELOC_SHORT} />
                                                    </When>
                                                    <Otherwise>
                                                        <FormattedMessage
                                                            id="web-components.ProductTableBestMortgageRatesTable.termTitle"
                                                            defaultMessage="{term}-yr"
                                                            values={{ term: row.term / 12 }}
                                                        />
                                                    </Otherwise>
                                                </Choose>
                                            </p>
                                            <If condition={MESSAGES[row.term]}>
                                                <TextAnchor
                                                    href={getCompareAllRatesLinks(row, intl)}
                                                    message={(
                                                        <p className="rh-text-xs rh-my-0">
                                                            <FormattedMessage
                                                                id="web-components.ProductTableBestMortgageRatesTable.CompareAllTermRates"
                                                                defaultMessage={'Compare all rates'}
                                                            />
                                                        </p>
                                                    )}
                                                    dataName="bestMortgageRatesTable-compareAllRates-cta"
                                                />
                                            </If>
                                        </td>
                                        <td className="table-cell-rate table-cell-with-border rh-px-0_75 rh-py-1">
                                            <If condition={row.fixed}>
                                                <ProductTableBestMortgageRatesTableCell
                                                    rate={row.fixed}
                                                    onRateClick={onRateClick}
                                                    isMoreDetailsInLightbox={isMoreDetailsInLightbox}
                                                />
                                            </If>
                                        </td>
                                        <td className="table-cell-rate table-cell-with-border rh-px-0_75 rh-py-1">
                                            <If condition={row.variable}>
                                                <ProductTableBestMortgageRatesTableCell
                                                    rate={row.variable}
                                                    onRateClick={onRateClick}
                                                    isMoreDetailsInLightbox={isMoreDetailsInLightbox}
                                                />
                                            </If>
                                        </td>
                                    </tr>
                                </For>
                            </tbody>
                        </table>
                        <If condition={isExpandable}>
                            <PageBehaviourButton
                                className="show-more-button rh-mx-auto"
                                data-name="BMR-table-expand"

                                onClick={toggleIsExpanded}
                                message={(
                                    <FormattedMessage
                                        id="web-components.ProductTableBestMortgageRatesTable.toggleExpanded"
                                        defaultMessage="{ isExpanded, select, true {Show less} other {Show more} }"
                                        values={{ isExpanded }}
                                    />
                                )}

                                size="small"
                                iconLocation="left"
                                iconComponent={isExpanded ? IconMinus : IconPlus}
                            />
                        </If>
                    </When>
                    <Otherwise>
                        <p className="rh-text-m rh-py-5 rh-text-align-center">
                            <FormattedMessage
                                id="web-components.ProductTableBestMortgageRatesTable.noRatesAvailable"
                                defaultMessage="No rates are available for this search criteria"
                            />
                        </p>
                    </Otherwise>
                </Choose>
            </div>
        </CondensedTheme>
    );
}

ProductTableBestMortgageRatesTable.propTypes = {
    rates: BestMortgageRatesPropType,
    onRateClick: PropTypes.func.isRequired,
    size: PropTypes.object.isRequired,
};

ProductTableBestMortgageRatesTable.defaultProps = {
    rates: [],
};

const TABLE_HEADER_CLASSES = 'table-header-cell rh-text-s weight-regular rh-fg-stone-darkest rh-text-align-left rh-px-0_75 rh-py-0_25';

// Requirement: Always hide 8, 9, & 25 year terms from the table
//  but we want them still in the DOM for SEO purposes
const EXCLUDED_TERMS = [ 96, 108, 300 ];

function isRowHidden(rate, isExpandable, isExpanded) {
    if (EXCLUDED_TERMS.includes(rate.term)) {
        return true;
    }

    // If we're not expandable, all rows should be visible
    if (!isExpandable) {
        return false;
    }

    // Hidden when the table is not expanded and we're not in the default visible terms
    return !isExpanded && !DEFAULT_VISIBLE_TERMS.includes(rate.term);
}

function getCompareAllRatesLinks(row, intl) {
    // If term is rendered without fixed or variable rates, don't show the link
    if (!(row?.fixed?.type || row?.variable?.type)) {
        return null;
    }

    const term = row.term / 12;
    const type = intl.formatMessage(RATE_TYPE_MESSAGES[row?.fixed?.type || row?.variable?.type]).toLowerCase();

    return intl.locale === Locales.FRENCH
        ? `/meilleurs-taux-hypothecaires/${term}-ans/${type}`
        : `/best-mortgage-rates/${term}-year/${type}`;
}

const StyledProductTableBestMortgageRatesTable = styled(ProductTableBestMortgageRatesTable)`
    background-color: ${Colours.COCONUT};

    > .best-mortgage-rates-table {
        width: 100%;
        border-collapse: collapse;


        .table-header-cell > .table-header-tooltip-container {
            display: flex;
            align-items: center;
        }

        .table-heading,
        .table-row:not(.hide-bottom-border) {
            border-bottom: 1px solid ${Colours.STONE};
        }

        .table-row {
            vertical-align: top;

            &.is-hidden {
                display: none;
            }

            > .table-cell-term {
                width: 15%;
            }

            > .table-cell-rate {
                width: 42.5%;
            }

            > .table-cell-with-border {
                position: relative;

                /* Using a pseudo element as a left border
                    since the designs have white space above
                    and below the border, which is not possible with
                    border-left properties. */
                &::before {
                    position: absolute;
                    top: ${Sizes.SPACING.ONE};
                    bottom: ${Sizes.SPACING.ONE};
                    left: 0;

                    content: '';

                    width: 1px;
                    background-color: ${Colours.STONE};
                }
            }

            .no-results {
                font-style: italic;
            }
        }
    }

    > .show-more-button {
        /* Specifiying padding here since the PageBehaviourButton styles
            have padding: 0 and we need to override */
        padding-top: ${Sizes.SPACING.ONE_AND_A_QUARTER};
        padding-bottom: ${Sizes.SPACING.ONE_AND_A_QUARTER};
    }
`;

const MESSAGES = defineMessages({
    FIXED_TOOLTIP: {
        id: 'web-components.ProductTableBestMortgageRatesTable.fixedTooltip',
        defaultMessage: '69% of Canadians choose a fixed-rate mortgage. A fixed rate means that your rate and monthly payment will stay constant throughout the term of your mortgage.',
    },
    VARIABLE_TOOLTIP: {
        id: 'web-components.ProductTableBestMortgageRatesTable.variableTooltip',
        defaultMessage: 'With a variable rate mortgage, your rate will change together with the prime lending rate (of your lender). This means your rate may go up and down throughout your term.',
    },
    [MORTGAGE_TERMS[0]]: {
        id: 'web-components.ProductTableBestMortgageRatesTable.1yearDescription',
        defaultMessage: 'Short commitment, best when fixed rates are unpredictable.',
    },
    [MORTGAGE_TERMS[1]]: {
        id: 'web-components.ProductTableBestMortgageRatesTable.2yearDescription',
        defaultMessage: 'Good option if you think fixed rates will be lower in 2 years.',
    },
    [MORTGAGE_TERMS[2]]: {
        id: 'web-components.ProductTableBestMortgageRatesTable.3yearDescription',
        defaultMessage: 'Middle of the road option. Good for those who are risk averse but considering a 5-year variable.',
    },
    [MORTGAGE_TERMS[3]]: {
        id: 'web-components.ProductTableBestMortgageRatesTable.4yearDescription',
        defaultMessage: 'Good option when rates are much lower than 5-year fixed',
    },
    [MORTGAGE_TERMS[4]]: {
        id: 'web-components.ProductTableBestMortgageRatesTable.5yearDescription',
        defaultMessage: 'Most popular term in Canada. Lowest rates are usually for this term.',
    },
    [MORTGAGE_TERMS[5]]: {
        id: 'web-components.ProductTableBestMortgageRatesTable.6yearDescription',
        defaultMessage: 'Higher rates than 7-year term and fewer lender options.',
    },
    [MORTGAGE_TERMS[6]]: {
        id: 'web-components.ProductTableBestMortgageRatesTable.7yearDescription',
        defaultMessage: 'Good if you think rates are going to increase significantly and have no plans to change your mortgage.',
    },
    [MORTGAGE_TERMS[9]]: {
        id: 'web-components.ProductTableBestMortgageRatesTable.10yearDescription',
        defaultMessage: 'Provides rate & payment stability, but at a very high cost.',
    },
});

export default withSize({ noPlaceholder: true })(StyledProductTableBestMortgageRatesTable);
